import React from 'react';
import {motion} from 'framer-motion';
import {Award, Clock, Headphones, ShieldCheck, ThumbsUp, Zap} from 'lucide-react';

const reasons = [
    {
        icon: Award,
        title: 'Certyfikowani specjaliści z regionu Mielca',
        desc: 'Masz pewność, że Twój system montuje doświadczony ekspert. Posiadamy certyfikaty i ponad 8 lat praktyki w branży zabezpieczeń.',
    },
    {
        icon: Clock,
        title: 'Szybka realizacja bez przestojów',
        desc: 'Nie czekasz tygodniami. Montaż systemu kamer w Mielcu i promieniu do 250 km realizujemy sprawnie, zazwyczaj w 1 do 2 dni robocze.',
    },
    {
        icon: ThumbsUp,
        title: 'Sprawdzony sprzęt z gwarancją',
        desc: 'Korzystasz z kamer i rejestratorów renomowanych marek (Hikvision, Dahua) z pełną gwarancją producenta do 3 lat, żadnych tanich zamienników.',
    },
    {
        icon: Headphones,
        title: 'Wsparcie techniczne, gdy go potrzebujesz',
        desc: 'Masz problem? Pomagamy zdalnie lub na miejscu w Mielcu i całym promieniu 250 km. Twój system monitoringu działa bez przerw, 24/7.',
    },
    {
        icon: Zap,
        title: 'Kamery 4K, ColorVu i AI, najnowsze technologie',
        desc: 'Otrzymujesz kamery z analityką obrazu AI, rozpoznawaniem ludzi i pojazdów, noktowizją w kolorze oraz podglądem na telefonie.',
    },
    {
        icon: ShieldCheck,
        title: 'Wszystko w jednym miejscu pod klucz',
        desc: 'Nie szukasz kilku firm. Od bezpłatnej wyceny, przez czysty montaż kamer w Mielcu, po serwis i wsparcie - wszystko załatwiasz u nas.',
    },
];

export function WhyUsSection() {
    return (
        <section className="w-full py-20 md:py-28 px-5">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.6}}
                    className="text-center mb-16"
                >
                    <div
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-primary/5 text-xs md:text-sm font-semibold text-primary mb-4">
                        Dlaczego klienci z Mielca i okolic do 250 km wybierają nas?
                    </div>
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                        Zaufaj <span className="gradient-text">specjalistom w Mielcu</span>
                    </h2>
                </motion.div>

                <motion.div
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    viewport={{once: true}}
                    transition={{duration: 0.5, delay: 0.1}}
                    className="mb-10"
                >
                    <div
                        className="rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/5 via-background to-amber-500/5 p-5 md:p-6 flex flex-col md:flex-row items-center gap-5 md:gap-6">
                        <a
                            href="https://www.orlyochrony.pl/profile-423481-onexo-monitoring-kamery-alarmy-i-it-mielec-profesjonalny-montaz"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 rounded-2xl bg-card border border-border/60 p-3 shadow-sm hover:shadow-md transition-shadow"
                            aria-label="Zobacz profil ONEXO w serwisie Orły Ochrony"
                        >
                            <img
                                src="https://www.orlyochrony.pl/images/projectImages/app_laureate_110_gold_113.png"
                                alt="Odznaka Orły Ochrony dla ONEXO"
                                title="ONEXO - Monitoring, Kamery, Alarmy i IT Mielec - Profesjonalny Montaż"
                                className="w-[110px] h-[110px] block"
                                loading="lazy"
                                decoding="async"
                            />
                        </a>

                        <div className="text-center md:text-left">
                            <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-primary/80 mb-2">
                                Wyróżnienie branżowe
                            </p>
                            <h3 className="text-xl md:text-2xl font-bold mb-2">
                                Laureat Orłów Ochrony
                            </h3>
                            <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                                Dodaliśmy miejsce na odznakę potwierdzającą zaufanie klientów.
                                Kliknij trofeum, aby przejść do pełnego profilu ONEXO i zobaczyć
                                wyróżnienie w serwisie Orły Ochrony.
                            </p>
                            <a
                                href="https://www.orlyochrony.pl/profile-423481-onexo-monitoring-kamery-alarmy-i-it-mielec-profesjonalny-montaz"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex mt-4 items-center gap-2 rounded-full border border-primary/20 bg-background px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors"
                            >
                                Zobacz odznakę i profil
                                <span aria-hidden="true">↗</span>
                            </a>
                        </div>
                    </div>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {reasons.map((reason, i) => {
                        const Icon = reason.icon;
                        return (
                            <motion.div
                                key={reason.title}
                                initial={{opacity: 0}}
                                whileInView={{opacity: 1}}
                                viewport={{once: true}}
                                transition={{duration: 0.4, delay: i * 0.08}}
                                className="flex items-start gap-4 p-5 rounded-2xl border border-border/50 bg-card hover:border-primary/20 transition-all group"
                            >
                                <div
                                    className="shrink-0 w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                    <Icon size={20} className="text-primary"/>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-base text-foreground mb-1">{reason.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{reason.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
